// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { consoleService } from '../../services/consoleService';
import JsonEditor from '../JsonEditor/JsonEditor';
import ComingSoon from '../ComingSoon/ComingSoon';
import LudoLeaderboard from '../LudoLeaderboard';
import { 
  ArenaMode, 
  CurrencyType, 
  Rank,
  LudoConfiguration,
  getArenaModeName,
  getCurrencyTypeName,
  getRankName,
  isValidArenaMode,
  isValidCurrencyType,
  isValidRank
} from '../../types/ludoSettings';
import { LudoSettingsClass, ArenaClass, CurrencyClass, ArenaRewardClass } from '../../classes/LudoSettings';
import './LudoSettings.scss';

const LudoSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [ludoSettings, setLudoSettings] = useState<LudoSettingsClass>(new LudoSettingsClass());
  const [originalLudoSettings, setOriginalLudoSettings] = useState<LudoSettingsClass>(new LudoSettingsClass());
  const [jsonError, setJsonError] = useState<string | null>(null);
  
  // Leaderboard configuration state
  const [leaderboardConfig, setLeaderboardConfig] = useState<string>('{}');
  const [leaderboardJsonError, setLeaderboardJsonError] = useState<string | null>(null);
  const [hasUnsavedLeaderboardChanges, setHasUnsavedLeaderboardChanges] = useState(false);
  
  const [activeTab, setActiveTab] = useState<'settings' | 'leaderboard'>('settings');
  const [modeToggles, setModeToggles] = useState<Record<string, boolean>>({
    '2P_ONLINE_ARENA': true,
    '2P_WITH_FRIENDS_ARENA': true,
    '4P_ONLINE_ARENA': true,
    '4P_WITH_FRIENDS_ARENA': true
  });

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        setLoading(true);
        
        // Try to load settings from RPC endpoint first
        let loadedSettings = null;
        try {
          // Serialize the request payload properly
          const serializedPayload = JSON.stringify({
            action: 'get'
          });
          
          const rpcResponse = await consoleService.callRpcFunction({
            id: 'ludo_arena_get',
            body: serializedPayload,
            user_id: '00000000-0000-0000-0000-000000000000',
            http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
          });

          console.log(rpcResponse);
          
          if (rpcResponse.body) {
            const responseData = JSON.parse(rpcResponse.body);
            // Handle both direct settings and wrapped response formats
            loadedSettings = responseData.data || responseData;
            console.log('Loaded Ludo settings from RPC:', loadedSettings);
          }
        } catch (rpcError: any) {
          console.warn('Failed to load settings from ludo_arena_get RPC endpoint:', rpcError);
          if (rpcError.response?.data?.message) {
            console.warn('RPC Error details:', rpcError.response.data.message);
          }
        }
        
        // Use loaded settings or fall back to defaults
        let ludoSettingsInstance: LudoSettingsClass;
        if (loadedSettings) {
          // Deserialize from server response using TypedJSON
          ludoSettingsInstance = LudoSettingsClass.fromObject(loadedSettings);
        } else {
          // Use default settings
          ludoSettingsInstance = new LudoSettingsClass();
        }
        
        setLudoSettings(ludoSettingsInstance);
        setOriginalLudoSettings(ludoSettingsInstance.clone());
        
        const defaultLeaderboard = getDefaultLeaderboardConfig();
        setLeaderboardConfig(JSON.stringify(defaultLeaderboard, null, 2));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize settings');
      } finally {
        setLoading(false);
      }
    };

    initializeSettings();
  }, []);

  // Validate ludoSettings when it changes
  useEffect(() => {
    try {
      const validationErrors = ludoSettings.validate();
      if (validationErrors.length > 0) {
        setJsonError(`Validation errors: ${validationErrors.join(', ')}`);
      } else {
        setJsonError(null);
      }
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid configuration');
    }
  }, [ludoSettings]);

  const loadLeaderboardConfig = async () => {
    try {
      // Load leaderboard config from backend or use default
      const config = getDefaultLeaderboardConfig();
      setLeaderboardConfig(JSON.stringify(config, null, 2));
    } catch (err) {
      console.error('Failed to load leaderboard config:', err);
    }
  };

  const getDefaultLeaderboardConfig = () => {
    return {
      "id": "ludo_leaderboard",
      "authoritative": true,
      "sort_order": "desc",
      "operator": "best",
      "reset_schedule": "0 0 * * 0",
      "metadata": {
        "game": "ludo",
        "description": "Ludo game leaderboard",
        "reward_tiers": {
          "1": { "coins": 100, "title": "Ludo Champion" },
          "2": { "coins": 75, "title": "Ludo Master" },
          "3": { "coins": 50, "title": "Ludo Expert" },
          "top_10": { "coins": 25, "title": "Ludo Player" }
        },
        "season_duration": "weekly",
        "min_games_required": 5,
        "scoring_factors": {
          "win_multiplier": 100,
          "kill_bonus": 10,
          "home_run_bonus": 5,
          "time_bonus_threshold": 600
        },
        "eligibility_rules": {
          "min_level": 1,
          "banned_users_excluded": true,
          "inactive_threshold_days": 30
        }
      }
    };
  };

  const getDefaultLudoSettings = () => {
    return {
      "arenas": {
        "2P_ONLINE_ARENA": {
          "name": "2P_ONLINE_ARENA",
          "mode": "2P_ONLINE",
          "enabled": true,
          "fee_cost": {
            "type": 2,
            "amount": 100
          },
          "rewards": {
            "1": [{ "type": 2, "amount": 160 }],
            "2": [{ "type": 2, "amount": 40 }],
            "5": [{ "type": 2, "amount": 10 }]
          },
          "json_config": {}
        },
        "2P_WITH_FRIENDS_ARENA": {
          "name": "2P_WITH_FRIENDS_ARENA",
          "mode": "2P_WITH_FRIENDS",
          "enabled": true,
          "fee_cost": {
            "type": 2,
            "amount": 100
          },
          "rewards": {
            "1": [{ "type": 2, "amount": 160 }],
            "2": [{ "type": 2, "amount": 40 }],
            "5": [{ "type": 2, "amount": 10 }]
          },
          "json_config": {}
        },
        "4P_ONLINE_ARENA": {
          "name": "4P_ONLINE_ARENA",
          "mode": "4P_ONLINE",
          "enabled": true,
          "fee_cost": {
            "type": 2,
            "amount": 100
          },
          "rewards": {
            "1": [{ "type": 2, "amount": 300 }],
            "2": [{ "type": 2, "amount": 60 }],
            "3": [{ "type": 2, "amount": 40 }],
            "5": [{ "type": 2, "amount": 20 }]
          },
          "json_config": {}
        },
        "4P_WITH_FRIENDS_ARENA": {
          "name": "4P_WITH_FRIENDS_ARENA",
          "mode": "4P_WITH_FRIENDS",
          "enabled": true,
          "fee_cost": {
            "type": 2,
            "amount": 100
          },
          "rewards": {
            "1": [{ "type": 2, "amount": 300 }],
            "2": [{ "type": 2, "amount": 60 }],
            "3": [{ "type": 2, "amount": 40 }],
            "5": [{ "type": 2, "amount": 20 }]
          },
          "json_config": {}
        }
      }
    };
  };

  const handleSaveLudoSettings = async () => {
    try {
      setLoading(true);
      
      // Validate settings before saving
      const validationErrors = ludoSettings.validate();
      if (validationErrors.length > 0) {
        setError(`Validation failed: ${validationErrors.join(', ')}`);
        return;
      }
      
      // Serialize settings to JSON for API call
      const serializedSettings = ludoSettings.toJSON();
      const settingsObject = ludoSettings.toObject();
      // Save to backend
      console.log(serializedSettings)
      await consoleService.updateGameSettings('ludo', settingsObject);
      
      // Call RPC endpoint to update arena configuration
      try {
        console.log('Sending serialized settings:', serializedSettings);

        const rpcResponse = await consoleService.callRpcFunction({
          id: 'ludo_arena_resetup',
          body: serializedSettings,
          user_id: '00000000-0000-0000-0000-000000000000',
          http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
        });
        console.log('Arena configuration updated via RPC:', rpcResponse);
      } catch (rpcError: any) {
        console.warn('Failed to call ludo_arena_resetup RPC endpoint:', rpcError);
        // Log the specific error but don't fail the entire save operation
        if (rpcError.response?.data?.message) {
          console.warn('RPC Error details:', rpcError.response.data.message);
        }
      }
      
      // Update original settings after successful save
      setOriginalLudoSettings(ludoSettings.clone());
      setHasUnsavedChanges(false);
      setError(null); // Clear any previous errors on successful save
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save Ludo settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLeaderboardConfig = async () => {
    try {
      setLoading(true);
      const parsedConfig = JSON.parse(leaderboardConfig);
      // Save leaderboard config to backend
      await consoleService.updateLeaderboardConfig('ludo', parsedConfig);
      setHasUnsavedLeaderboardChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save leaderboard configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleLudoSettingsChange = (newSettings: LudoSettingsClass) => {
    setLudoSettings(newSettings);
    // Check if settings have changed from original
    const hasChanged = newSettings.hasChangedFrom(originalLudoSettings);
    console.log(hasChanged)
    setHasUnsavedChanges(hasChanged);
  };

  const resetLudoSettings = () => {
    const defaultSettings = new LudoSettingsClass();
    setLudoSettings(defaultSettings);
    setJsonError(null);
    setHasUnsavedChanges(true);
  };

  const refreshLudoSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load settings from RPC endpoint with serialized request
      const serializedPayload = JSON.stringify({
        action: 'get'
      });
      
      const rpcResponse = await consoleService.callRpcFunction( {
        id: 'ludo_arena_get',
        body: serializedPayload,
        user_id: '00000000-0000-0000-0000-000000000000',
        http_key: 'game_asDFGHyhu_http487ewmdc04356lvdme09'
      });
      
      if (rpcResponse.body) {
        const responseData = JSON.parse(rpcResponse.body);
        // Handle both direct settings and wrapped response formats
        const loadedSettings = responseData.data || responseData;
        
        // Deserialize from server response using TypedJSON
        const ludoSettingsInstance = LudoSettingsClass.fromObject(loadedSettings);
        setLudoSettings(ludoSettingsInstance);
        setOriginalLudoSettings(ludoSettingsInstance.clone());
        setJsonError(null);
        setHasUnsavedChanges(false);
        console.log('Refreshed Ludo settings from RPC:', loadedSettings);
      } else {
        throw new Error('No payload received from RPC endpoint');
      }
    } catch (rpcError: any) {
      console.error('Failed to refresh settings from ludo_arena_get RPC endpoint:', rpcError);
      setError('Failed to refresh settings from server. Using current settings.');
      if (rpcError.response?.data?.message) {
        console.error('RPC Error details:', rpcError.response.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Define field metadata for enum support
  const getFieldMetadata = () => {
    return {
      // Arena mode enums
      'arenas.2P_ONLINE_ARENA.mode': {
        type: 'enum' as const,
        enumOptions: Object.values(ArenaMode).map(mode => ({
          value: mode,
          label: getArenaModeName(mode)
        })),
        description: 'Arena game mode (2P/4P, Online/Friends)'
      },
      'arenas.2P_WITH_FRIENDS_ARENA.mode': {
        type: 'enum' as const,
        enumOptions: Object.values(ArenaMode).map(mode => ({
          value: mode,
          label: getArenaModeName(mode)
        })),
        description: 'Arena game mode (2P/4P, Online/Friends)'
      },
      'arenas.4P_ONLINE_ARENA.mode': {
        type: 'enum' as const,
        enumOptions: Object.values(ArenaMode).map(mode => ({
          value: mode,
          label: getArenaModeName(mode)
        })),
        description: 'Arena game mode (2P/4P, Online/Friends)'
      },
      'arenas.4P_WITH_FRIENDS_ARENA.mode': {
        type: 'enum' as const,
        enumOptions: Object.values(ArenaMode).map(mode => ({
          value: mode,
          label: getArenaModeName(mode)
        })),
        description: 'Arena game mode (2P/4P, Online/Friends)'
      },
      // Currency type enums for fee costs
      'arenas.2P_ONLINE_ARENA.fee_cost.type': {
        type: 'enum' as const,
        enumOptions: Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => ({
          value: type,
          label: getCurrencyTypeName(type as CurrencyType)
        })),
        description: 'Currency type for entry fee'
      },
      'arenas.2P_WITH_FRIENDS_ARENA.fee_cost.type': {
        type: 'enum' as const,
        enumOptions: Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => ({
          value: type,
          label: getCurrencyTypeName(type as CurrencyType)
        })),
        description: 'Currency type for entry fee'
      },
      'arenas.4P_ONLINE_ARENA.fee_cost.type': {
        type: 'enum' as const,
        enumOptions: Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => ({
          value: type,
          label: getCurrencyTypeName(type as CurrencyType)
        })),
        description: 'Currency type for entry fee'
      },
      'arenas.4P_WITH_FRIENDS_ARENA.fee_cost.type': {
        type: 'enum' as const,
        enumOptions: Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => ({
          value: type,
          label: getCurrencyTypeName(type as CurrencyType)
        })),
        description: 'Currency type for entry fee'
      },
      // Currency type enums for rewards (dynamic based on ranks)
      ...generateRewardMetadata()
    };
  };

  // Helper function to generate reward metadata for all arenas and ranks
  const generateRewardMetadata = () => {
    const metadata: Record<string, any> = {};
    const arenas = ['2P_ONLINE_ARENA', '2P_WITH_FRIENDS_ARENA', '4P_ONLINE_ARENA', '4P_WITH_FRIENDS_ARENA'];
    
    arenas.forEach(arena => {
      // For each possible rank (1-4)
      for (let rank = 1; rank <= 4; rank++) {
        // Currency type for rewards
        metadata[`arenas.${arena}.rewards.${rank}.0.type`] = {
          type: 'enum' as const,
          enumOptions: Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => ({
            value: type,
            label: getCurrencyTypeName(type as CurrencyType)
          })),
          description: `Currency type for ${getRankName(rank as Rank)} reward`
        };
        
        // Amount field for rewards
        metadata[`arenas.${arena}.rewards.${rank}.0.amount`] = {
          type: 'number' as const,
          description: `Reward amount for ${getRankName(rank as Rank)}`
        };
      }
    });
    
    return metadata;
  };

  // Update arena field using class methods
  const updateArenaField = (arenaKey: string, field: string, value: any) => {
    const updatedSettings = ludoSettings.clone();
    updatedSettings.updateArenaField(arenaKey, field, value);
    handleLudoSettingsChange(updatedSettings);
  };

  // Render arena configuration as form fields
  const renderArenaFields = () => {
    if (!ludoSettings || !ludoSettings.arenas) {
      return <div className="no-config">No arena configuration available</div>;
    }

    return (
      <div className="arena-fields-container">
        {Object.entries(ludoSettings.arenas).map(([arenaKey, arena]) => (
          <div key={arenaKey} className="arena-section">
            <div className="arena-header">
              <h3 className="arena-title">
                {getArenaModeName(arena.mode as ArenaMode)}
              </h3>
              <div className="arena-badge">
                {arena.mode.includes('2P') ? '👥 2 Players' : '👥👥 4 Players'}
              </div>
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={modeToggles[arenaKey]}
                  onChange={() => handleToggleChange(arenaKey)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            
            <div className="arena-fields">
              {/* Entry Fee */}
              <div className="field-group entry-fee-group">
                <label className="field-label">
                  <span className="label-icon">💰</span>
                  Entry Fee
                </label>
                <div className="fee-fields">
                  <select
                    value={arena.fee_cost.type}
                    onChange={(e) => updateArenaField(arenaKey, 'fee_cost.type', parseInt(e.target.value))}
                    className="form-control currency-select"
                  >
                    {Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => (
                      <option key={type} value={type}>
                        {getCurrencyTypeName(type as CurrencyType)}
                      </option>
                    ))}
                  </select>
                  <div className="amount-input-wrapper">
                    <input
                      type="number"
                      value={arena.fee_cost.amount}
                      onChange={(e) => updateArenaField(arenaKey, 'fee_cost.amount', parseInt(e.target.value))}
                      className="form-control amount-input"
                      min="0"
                      placeholder="Amount"
                    />
                    <span className="currency-indicator"></span>
                  </div>
                </div>
              </div>

              {/* Rewards */}
              <div className="field-group rewards-group">
                <label className="field-label">
                  <span className="label-icon">🏆</span>
                  Rank Rewards
                </label>
                <div className="rewards-container">
                  {Object.entries(arena.rewards).map(([rank, rewardArray]) => {
                    const reward = rewardArray[0]; // Get first reward
                    const rankNum = parseInt(rank);
                    const rankEmojis: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉', 4: '🏅', 5: '🎁' };
                    const rankNames: Record<number, string> = { 1: '1st Place', 2: '2nd Place', 3: '3rd Place', 4: '4th Place', 5: 'Consolation' };
                    
                    return (
                      <div key={rank} className="reward-row">
                        <span className="rank-label">
                          {rankEmojis[rankNum] || '🏅'} {rankNames[rankNum] || `${rank}th Place`}
                        </span>
                        <select
                          value={reward.type}
                          onChange={(e) => updateArenaField(arenaKey, `rewards.${rank}.0.type`, parseInt(e.target.value))}
                          className="form-control currency-select"
                        >
                          {Object.values(CurrencyType).filter(val => typeof val === 'number').map(type => (
                            <option key={type} value={type}>
                              {getCurrencyTypeName(type as CurrencyType)}
                            </option>
                          ))}
                        </select>
                        <div className="amount-input-wrapper">
                          <input
                            type="number"
                            value={reward.amount}
                            onChange={(e) => updateArenaField(arenaKey, `rewards.${rank}.0.amount`, parseInt(e.target.value))}
                            className="form-control amount-input"
                            min="0"
                            placeholder="Reward"
                          />
                          <span className="currency-indicator"></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleToggleChange = (arenaKey: string) => {
    setModeToggles(prevState => ({
      ...prevState,
      [arenaKey]: !prevState[arenaKey]
    }));
  };

  const handleLeaderboardConfigChange = (value: string) => {
    setLeaderboardConfig(value);
    setHasUnsavedLeaderboardChanges(true);
    
    // Validate JSON
    try {
      JSON.parse(value);
      setLeaderboardJsonError(null);
    } catch (err) {
      setLeaderboardJsonError(err instanceof Error ? err.message : 'Invalid JSON format');
    }
  };

  const resetLeaderboardConfig = () => {
    const defaultConfig = getDefaultLeaderboardConfig();
    setLeaderboardConfig(JSON.stringify(defaultConfig, null, 2));
    setLeaderboardJsonError(null);
    setHasUnsavedLeaderboardChanges(true);
  };

  const validateLeaderboardConfig = () => {
    try {
      const parsed = JSON.parse(leaderboardConfig);
      
      // Basic validation for required fields
      const requiredFields = ['id', 'authoritative', 'sort_order', 'operator'];
      const missingFields = requiredFields.filter(field => !parsed[field]);
      
      if (missingFields.length > 0) {
        setLeaderboardJsonError(`Missing required fields: ${missingFields.join(', ')}`);
        return false;
      }
      
      setLeaderboardJsonError(null);
      return true;
    } catch (err) {
      setLeaderboardJsonError(err instanceof Error ? err.message : 'Invalid JSON format');
      return false;
    }
  };

  const validateLudoSettings = () => {
    try {
      // Use the class validation method instead of JSON parsing
      const validationErrors = ludoSettings.validate();
      
      if (validationErrors.length > 0) {
        setJsonError(`Validation errors: ${validationErrors.join(', ')}`);
        return false;
      }
      
      setJsonError(null);
      return true;
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid configuration');
      return false;
    }
  };

  if (loading) {
    return (
      <div className="ludo-settings">
        <div className="loading-spinner">Loading Ludo settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ludo-settings">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ludo-settings">
      <div className="ludo-settings-header">
        <h1>🎲 Ludo Game Settings</h1>
        
        {(hasUnsavedChanges || hasUnsavedLeaderboardChanges) && (
          <div className="unsaved-changes-banner">
            <span>⚠️ You have unsaved changes</span>
            <div className="save-buttons">
              {hasUnsavedChanges && (
                <button onClick={handleSaveLudoSettings} className="btn btn-primary" disabled={loading || !!jsonError}>
                  {loading ? 'Saving...' : 'Save Game Settings'}
                </button>
              )}
              {hasUnsavedLeaderboardChanges && (
                <button onClick={handleSaveLeaderboardConfig} className="btn btn-primary" disabled={loading || !!leaderboardJsonError}>
                  {loading ? 'Saving...' : 'Save Leaderboard'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon">🎲</span>
          <span className="tab-text">Arena Configuration</span>
        </button>
        <button 
          className={`tab-button ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          <span className="tab-icon">🏆</span>
          <span className="tab-text">Leaderboard System</span>
        </button>
      </div>

      <div className="ludo-settings-content">
        {/* Game Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-section">
            {/* Settings Actions */}
            <div className="settings-actions">
              <div className="action-buttons">
              </div>
              {jsonError && (
                <div className="validation-error">
                  <span className="error-icon">❌</span>
                  <span className="error-text">{jsonError}</span>
                </div>
              )}
            </div>

            {renderArenaFields()}
          </div>
        )}

        {/* Leaderboard Configuration Tab */}
        {activeTab === 'leaderboard' && (
          <div className="leaderboard-section">
            <LudoLeaderboard leaderboardId="total_earned_coins_ludo" />
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default LudoSettings;
